/**
 * Wochen- und Monatsziele (Roadmap F-16). Eingaben werden hier geprueft -
 * die Oberflaeche verlaesst sich darauf, dass nichts Unsinniges ankommt.
 */
import { db } from '@/db/schema';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import { fokusKeyGueltig, type FocusGoal, type FokusArt, type FokusStatus } from '@/domain/planning/focus';
import { nowInstant } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import type { ID } from '@/domain/types';

const STATUS: FokusStatus[] = ['offen', 'erledigt', 'uebernommen', 'gestrichen'];

export async function focusGoalsOf(art: FokusArt, key: string): Promise<FocusGoal[]> {
  const rows = await db.focusGoals.where('[art+key]').equals([art, key]).toArray();
  return rows.sort((a, b) => a.order - b.order);
}

export async function addFocusGoal(art: FokusArt, key: string, title: string, goalId?: ID | null): Promise<FocusGoal | null> {
  const t = title.trim();
  if (!t || !fokusKeyGueltig(art, key)) return null;
  const vorhandene = await focusGoalsOf(art, key);
  const ts = nowInstant();
  const g: FocusGoal = {
    id: newId(), art, key, title: t, status: 'offen',
    order: (vorhandene.at(-1)?.order ?? 0) + 1, createdAt: ts, updatedAt: ts,
    ...(goalId ? { goalId } : {}),
  };
  await db.focusGoals.add(g);
  return g;
}

/** Titel und grosses Ziel aendern; `goalId: null` loest die Verknuepfung. */
export async function updateFocusGoal(id: ID, patch: { title?: string; goalId?: ID | null }): Promise<void> {
  await db.focusGoals.where('id').equals(id).modify((g) => {
    // Ein leerer Titel loescht nicht versehentlich - er wird ignoriert.
    if (patch.title?.trim()) g.title = patch.title.trim();
    if (patch.goalId !== undefined) {
      if (patch.goalId) g.goalId = patch.goalId;
      else delete g.goalId;
    }
    g.updatedAt = nowInstant();
  });
}

export async function setFocusStatus(id: ID, status: FokusStatus): Promise<void> {
  if (!STATUS.includes(status)) return;
  await db.focusGoals.update(id, { status, updatedAt: nowInstant() });
}

/** Abhaken und wieder oeffnen. */
export async function toggleFocusDone(id: ID): Promise<void> {
  const g = await db.focusGoals.get(id);
  if (!g) return;
  await setFocusStatus(id, g.status === 'erledigt' ? 'offen' : 'erledigt');
}

export async function deleteFocusGoal(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'focusGoals', zeileIds: [id] }, () => db.focusGoals.delete(id));
}

/**
 * Einen offenen Fokuspunkt in einen anderen Zeitraum mitnehmen: dort als
 * neuer, offener Punkt; der alte gilt als "uebernommen". Nur einmal moeglich.
 */
export async function uebernehmeFocusGoal(id: ID, zielKey: string): Promise<FocusGoal | null> {
  return db.transaction('rw', db.focusGoals, async () => {
    const alt = await db.focusGoals.get(id);
    if (!alt || alt.status !== 'offen' || !fokusKeyGueltig(alt.art, zielKey) || zielKey === alt.key) return null;
    const neu = await addFocusGoal(alt.art, zielKey, alt.title, alt.goalId);
    if (!neu) return null;
    await db.focusGoals.update(neu.id, { uebernommenVon: alt.id });
    await setFocusStatus(alt.id, 'uebernommen');
    return { ...neu, uebernommenVon: alt.id };
  });
}
